import React, { useState, useEffect, useRef } from 'react';
import { calculate } from './logic.js';
import { GoogleGenerativeAI } from '@google/generative-ai';

// Converts a File object to a GoogleGenerativeAI.Part object.
async function fileToGenerativePart(file) {
  const base64EncodedDataPromise = new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  });
  return {
    inlineData: {
      data: await base64EncodedDataPromise,
      mimeType: file.type,
    },
  };
}

const Hint = ({ text }) => {
  const [showHint, setShowHint] = useState(false);
  const hintRef = useRef(null);

  const handleClickOutside = (event) => {
    if (hintRef.current && !hintRef.current.contains(event.target)) {
      setShowHint(false);
    }
  };

  useEffect(() => {
    if (showHint) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHint]);

  return (
    <div className="hint-container" ref={hintRef}>
      <span className="hint-icon" onClick={() => setShowHint(!showHint)}>ⓘ</span>
      {showHint && <div className="hint-popup">{text}</div>}
    </div>
  );
};

const dynamicHints = {
  total_annual_income: (people) => `Sum of annual incomes for all people.\nCalculation: ${people.map(p => p.annual_income.toFixed(2)).join(' + ')}`,

  total_annual_taxable_income: (people, results) => `Total annual income minus total non-taxable deductions for all people.\nCalculation: ${results.total.total_annual_income.toFixed(2)} (Total Annual Income) - (${results.total.non_taxable_income_per_person.toFixed(2)} (Non-Taxable Per Person) * ${people.length})`,

  non_taxable_income_per_person: (people, results) => `The average non-taxable income across all people.\nCalculation: ${results.total.total_non_taxable_income.toFixed(2)} (Total Non-Taxable) / ${people.length} (People)`,

  expected_marginal_federal_tax_rate: (results) => `The effective federal tax rate on the total annual income.\nCalculation: ${results.total.taxes_due.toFixed(2)} (Total Taxes Due) / ${results.total.total_annual_income.toFixed(2)} (Total Annual Income)`,

  expected_marginal_federal_tax_rate_on_taxable_salary: (results) => `The effective federal tax rate on the total annual taxable income.\nCalculation: ${results.total.taxes_due.toFixed(2)} (Total Taxes Due) / ${results.total.total_annual_taxable_income.toFixed(2)} (Total Annual Taxable Income)`,

  annual_income: () => "Your total expected income for the entire year, including base salary, bonuses, and stock compensation.",
  yearly_base: () => "Your fixed annual salary, excluding any bonuses or stock compensation.",
  total_income_till_now: () => "Your total earnings for the year so far, before any taxes or deductions. This typically includes your regular salary, any bonuses, and income from vested stock. You can usually find this on your paystub as 'YTD Gross'.",
  regular_pay_till_now: () => "Regular gross pay received so far this year, excluding bonuses and stock.",

  total_taxable_income_till_now: () => "Year-to-date taxable income, after pre-tax deductions.",
  federal_taxes_till_now: () => "Year-to-date federal taxes paid.",

  federal_taxes_by_total_income_rate: (result) => `The effective federal tax rate on the total income till now.\nCalculation: (${result.federal_taxes_till_now.toFixed(2)} / ${result.total_income_till_now.toFixed(2)}) * 100`,

  federal_taxes_by_total_taxable_income_rate: (result) => `The effective federal tax rate on the total taxable income till now.\nCalculation: (${result.federal_taxes_till_now.toFixed(2)} / ${result.total_taxable_income_till_now.toFixed(2)}) * 100`,

  supplemental_tax_rate: () => "The tax rate applied to supplemental income like bonuses and stock vesting. This is often a flat rate (e.g., 22% or 37%).",
  extra_withholdings: () => "Any extra flat amount you have instructed your employer to withhold from each paycheck.",
  '401k_contribution': () => 'Your total annual contribution to your 401K.',
  'hsa_contribution': () => 'Your total annual contribution to your HSA.',
  'dcfsa_contribution': () => 'Your total annual contribution to your Dependent Care FSA (DCFSA).',
  'transit_contribution': () => 'Your total annual pre-tax contribution for transit expenses.',

  non_taxable_income: (person) => `Sum of 401K, HSA, DCFSA, and pretax transit contributions for this person. 401K is multiplied by the company match multiplier.\nCalculation: (401K Contribution with match from ${person['401k_match_type']}) + ${person['hsa_contribution']} (HSA) + ${person['dcfsa_contribution']} (DCFSA) + ${person['transit_contribution']} (Transit)`,

  money_difference_till_now: (result) => `The difference between what should have been paid in federal taxes and what has been paid, based on the expected tax rate for this person.\nCalculation: (${result.expected_marginal_federal_tax_rate_person.toFixed(2)}% - ${result.federal_taxes_by_total_taxable_income_rate.toFixed(2)}%) * ${result.total_taxable_income_till_now.toFixed(2)}`,

  remaining_income: (result) => `The total income this person is expected to receive for the rest of the year.\nCalculation: ${result.remaining_base_income.toFixed(2)} (Remaining Base) + ${result.remaining_stock_income.toFixed(2)} (Remaining Stock)`,

  regular_pay_tax_rate: (result) => `The estimated tax rate on this person's regular base pay.\nCalculation: (Taxes due on ${result.regular_pay_taxable_income.toFixed(2)}) / ${result.regular_pay_taxable_income.toFixed(2)}`,

  remaining_planned_taxes: (result, person, remaining_pay_cycles) => `Taxes this person is expected to pay for the rest of the year based on current settings.\nCalculation: (${remaining_pay_cycles} * ${result.extra_withholdings}) [Extra] + ((${result.regular_pay_tax_rate.toFixed(2)}% * ${result.remaining_base_income.toFixed(2)})) [Base] + (${result.remaining_stock_income.toFixed(2)} * ${result.withholding_rate}) [Stock]`,

  remaining_required_taxes: (result) => `The amount of tax this person still needs to pay to meet their total estimated annual tax burden.\nCalculation: ((${result.annual_income.toFixed(2)} - ${result.non_taxable_income_per_person.toFixed(2)}) * ${result.expected_marginal_federal_tax_rate_person.toFixed(2)}%) - ${result.federal_taxes_till_now.toFixed(2)}`,

  extra_required_taxes: (result) => `The shortfall or surplus in planned tax payments for the rest of the year.\nCalculation: ${result.remaining_required_taxes.toFixed(2)} (Required) - ${result.remaining_planned_taxes.toFixed(2)} (Planned)`,

  deficit_till_now: (result) => `The total current tax deficit, combining the deficit from income already received and the projected deficit for the rest of the year.\nCalculation: ${result.money_difference_till_now.toFixed(2)} (Deficit Till Now) + ${result.extra_required_taxes.toFixed(2)} (Projected Deficit)`,

  deficit_per_remaining_pay_cycle: (result, person, remaining_pay_cycles) => `The amount by which to increase extra withholding per pay cycle to cover the total deficit.\nCalculation: ${result.deficit_till_now.toFixed(2)} (Total Deficit) / ${remaining_pay_cycles} (Remaining Pay Cycles)`,
  remaining_pay_cycles: () => 'The number of pay cycles remaining in the year for everyone.'
};

dynamicHints.section_annual_salary = () => 'Information about the total expected salary for the entire year.';
dynamicHints.section_salary_received = () => 'Information about salary and taxes paid from the beginning of the year until today.';
dynamicHints.section_withholding_details = () => 'Your current W-4 withholding settings.';
dynamicHints.section_deductions = () => 'Pre-tax deductions and contributions for retirement and health savings.';

const WithholdingCalculator = () => {
  const [people, setPeople] = useState([
    {
      name: 'Person 1',
      annual_income: 0,
      yearly_base: 0,
      regular_pay_till_now: 0,

      total_income_till_now: 0,
      total_taxable_income_till_now: 0,
      federal_taxes_till_now: 0,
      extra_withholdings: 0,
      supplemental_tax_rate: 0,
      '401k_contribution': 0,
      '401k_match_type': 'Multiplier',
      '401k_match_value': 1.5,
      'hsa_contribution': 0,
      'dcfsa_contribution': 0,
      'transit_contribution': 0,
      isEditingName: false,
    },
    {
      name: 'Person 2',
      annual_income: 0,
      yearly_base: 0,
      regular_pay_till_now: 0,

      total_income_till_now: 0,
      total_taxable_income_till_now: 0,
      federal_taxes_till_now: 0,
      extra_withholdings: 0,
      supplemental_tax_rate: 0,
      '401k_contribution': 0,
      '401k_match_type': 'Multiplier',
      '401k_match_value': 1.5,
      'hsa_contribution': 0,
      'dcfsa_contribution': 0,
      'transit_contribution': 0,
      isEditingName: false,
    },
  ]);

  const [remainingPayCycles, setRemainingPayCycles] = useState(() => {
    const today = new Date();
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    const diffInTime = endOfYear.getTime() - today.getTime();
    const diffInWeeks = diffInTime / (1000 * 3600 * 24 * 7);
    return Math.floor(diffInWeeks / 2);
  });
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [copied, setCopied] = useState(false);
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [isLoadingGeminiResponse, setIsLoadingGeminiResponse] = useState(false);

  const handleGeminiUpload = async (file, personIndex) => {
    if (!geminiApiKey) {
      alert('Please enter your Gemini API key.'); // Keep this alert for now, can be refactored later
      return null; // Return null if no API key
    }

    console.log('Gemini API Request:', { file: file.name, personIndex });

    try {
      const genAI = new GoogleGenerativeAI(geminiApiKey);
      const model = genAI.getGenerativeModel({ model: "gemini-3.7-flash" });

      const imageParts = [await fileToGenerativePart(file)];

      const promptParts = [
        {
          text: `Analyze the provided image/PDF.
    1.  **Determine if the document is a paystub.** Look for common paystub elements like "Gross Pay", "Net Pay", "YTD", "Taxes", "Deductions", "Employee Name", "Pay Period", etc.
    2.  **If it is a paystub:**
        *   Extract the following financial information:
            *   'total_income_till_now' (total earnings for the year so far, before any taxes or deductions, including base salary, bonuses, and stock compensation)
            *   'regular_pay_till_now' (regular gross pay received so far this year, excluding bonuses and stock)

            *   'total_taxable_income_till_now' (year-to-date taxable income, after pre-tax deductions)
            *   'federal_taxes_till_now' (year-to-date federal taxes paid)
        *   Include "is_paystub": true in the JSON output.
    3.  **If it is NOT a paystub:**
        *   Extract "annual_income" (your total expected income for the entire year for ${new Date().getFullYear()}, including base salary, bonuses, and stock compensation)
        *   Extract "yearly_base" (your fixed annual salary for ${new Date().getFullYear()}, excluding any bonuses or stock compensation)
        *   Include "is_paystub": false in the JSON output.
    4.  **Output Format:** Provide the extracted information in a JSON format. If a numerical value is not found, use 0.0. If a boolean value is not found, use false.` },
        ...imageParts,
      ];

      const result = await model.generateContent({
        contents: [{
          parts: promptParts
        }],
      });
      const response = await result.response;
      const text = response.text();

      console.log('Gemini API Raw Response Text:', text);

      // Attempt to parse the text as JSON
      let geminiData;
      try {
        const jsonMatch = text.match(/```json\n([\s\S]*?)\n```/);
        if (jsonMatch && jsonMatch[1]) {
          geminiData = JSON.parse(jsonMatch[1]);
        } else {
          throw new Error("No JSON found within ```json block.");
        }
      } catch (jsonError) {
        console.error('Failed to parse Gemini response as JSON:', jsonError);
        // Do not set modal content here, let handleFileChange do it
        return null; // Return null on JSON parsing error
      }

      console.log('Gemini API Parsed Response:', geminiData);

      // Process the data and update the UI
      setPeople(prevPeople => {
        const newPeople = [...prevPeople];
        const targetPerson = { ...newPeople[personIndex] };

        // Update the person's data based on the Gemini response
        for (const key in geminiData) {
          if (targetPerson.hasOwnProperty(key)) {
            targetPerson[key] = parseFloat(geminiData[key]) || 0;
          }
        }
        newPeople[personIndex] = targetPerson;
        return newPeople;
      });

      return geminiData; // Return the extracted data
    } catch (error) {
      console.error('Gemini API Error:', error);
      alert('Failed to process the file with Gemini. Check console for details.'); // Keep alert for immediate feedback
      return null; // Return null on API error
    }
  };

  const handleFileChange = async (event, personIndex) => {
    const files = event.target.files;
    if (files.length === 0) return;

    setIsLoadingGeminiResponse(true); // Start loading for all files
    let allGeminiData = [];

    try {
      // Make Gemini requests in parallel
      const geminiPromises = Array.from(files).map(file => handleGeminiUpload(file, personIndex));
      const results = await Promise.all(geminiPromises);

      // Filter out null results (from errors or no API key)
      allGeminiData = results.filter(data => data !== null);

      // Consolidate and display results in a single modal
      if (allGeminiData.length > 0) {
        const consolidatedContent = [];
        const personName = people[personIndex].name;
        consolidatedContent.push(
          <p key="intro">For <strong>{personName}</strong>, the following fields were updated based on Gemini's analysis:</p>
        );

        allGeminiData.forEach((data, fileIndex) => {
          // Removed: consolidatedContent.push(<h4 key={`file-header-${fileIndex}`}>File ${fileIndex + 1}</h4>);
          let fileHasUsefulData = false;
          for (const key in data) {
            if (data.hasOwnProperty(key) && key !== 'is_paystub' && data[key] !== 0) {
              consolidatedContent.push(
                <p key={`${fileIndex}-${key}`}>
                  <strong>{formatLabel(key)}:</strong> {data[key]}
                </p>
              );
              fileHasUsefulData = true;
            }
          }
          if (!fileHasUsefulData) {
            consolidatedContent.push(<p key={`no-data-${fileIndex}`}>No useful data extracted from this file.</p>);
          }
        });

        setModalContent(consolidatedContent);
      } else {
        setModalContent('Gemini was not able to extract any useful data from the uploaded files.');
      }
      setShowModal(true); // Show modal once with consolidated content

    } catch (error) {
      console.error('Error processing files with Gemini:', error);
      setModalContent('Failed to process files with Gemini. Check console for details.');
      setShowModal(true);
    } finally {
      setIsLoadingGeminiResponse(false); // End loading for all files
    }
  };

  const Modal = ({ show, onClose, content }) => {
    if (!show) {
      return null;
    }

    return (
      <div className="modal">
        <div className="modal-content">
          <span className="close" onClick={onClose}>&times;</span>
          <div>{content}</div>
        </div>
      </div>
    );
  };



  const handlePdfParsed = (parsedData, personIndex) => {
    setPeople(prevPeople => {
      const newPeople = [...prevPeople];
      const targetPerson = { ...newPeople[personIndex] };

      if (parsedData.grossPay !== undefined) {
        targetPerson.annual_income = parsedData.grossPay; // Map grossPay to annual_income for now
      }
      if (parsedData.netPay !== undefined) {
        targetPerson.total_income_till_now = parsedData.netPay; // Map netPay to total_income_till_now for now
      }
      // Add more mappings here as the parsing logic in PdfUploadParser becomes more sophisticated

      newPeople[personIndex] = targetPerson;
      return newPeople;
    });
  };

  const handleToggleEditName = (index) => {
    const newPeople = [...people];
    newPeople[index].isEditingName = !newPeople[index].isEditingName;
    setPeople(newPeople);
  };

  const handlePersonChange = (index, field, value) => {
    const newPeople = [...people];
    if (field === 'name') {
      newPeople[index][field] = value;
    } else if (field === '401k_match_type' || field === 'isEditingName') {
      newPeople[index][field] = value;
    } else if (field === 'supplemental_tax_rate') {
      let parsedValue = parseFloat(value);
      if (isNaN(parsedValue)) {
        newPeople[index][field] = ''; // Allow empty string for clearing input
      } else if (parsedValue > 1) {
        newPeople[index][field] = 1;
      } else if (parsedValue < 0) {
        newPeople[index][field] = 0;
      } else {
        newPeople[index][field] = parsedValue;
      }
    } else {
      newPeople[index][field] = parseFloat(value) || 0;
    }
    setPeople(newPeople);
    setResults(null);
  };

  const handleCalculate = () => {
    const calculatedResults = calculate({ remaining_pay_cycles: remainingPayCycles }, people);
    setResults(calculatedResults);
    setShowResults(true);
  };

  const handleCopy = () => {
    if (results) {
      let copyText = "";
      copyText += "Totals:\n";
      copyText += `Annual Income: ${results.total.total_annual_income.toFixed(2)}\n`;
      copyText += `Taxable Income: ${results.total.total_annual_taxable_income.toFixed(2)}\n`;
      copyText += `Non-Taxable Income per Person: ${results.total.non_taxable_income_per_person.toFixed(2)}\n`;
      copyText += `Expected Marginal Federal Tax Rate: ${results.total.expected_marginal_federal_tax_rate.toFixed(2)}%\n`;
      copyText += `Expected Marginal Federal Tax Rate on Taxable Salary: ${results.total.expected_marginal_federal_tax_rate_on_taxable_salary.toFixed(2)}%\n\n`;

      results.people.forEach(result => {
        copyText += `--- ${result.name} ---\n`;
        copyText += "Personal Details:\n";
        copyText += `Annual Income: ${result.annual_income.toFixed(2)}\n`;
        copyText += `Total Income Till Now: ${result.total_income_till_now.toFixed(2)}\n`;
        copyText += `Total Taxable Till Now: ${result.total_taxable_income_till_now.toFixed(2)}\n`;
        copyText += `Federal Taxes Till Now: ${result.federal_taxes_till_now.toFixed(2)}\n`;
        copyText += `Federal Taxes by Total Income Rate: ${result.federal_taxes_by_total_income_rate.toFixed(2)}%\n`;
        copyText += `Federal Taxes by Total Taxable Income Rate: ${result.federal_taxes_by_total_taxable_income_rate.toFixed(2)}%\n`;
        copyText += `Withholding Rate: ${result.withholding_rate.toFixed(2)}\n`;
        copyText += `Extra Withholdings: ${result.extra_withholdings.toFixed(2)}\n\n`;

        copyText += "Suggestion:\n";
        copyText += `Federal Tax Deficit Till Now: ${result.money_difference_till_now.toFixed(2)}\n`;
        copyText += `Remaining Income: ${result.remaining_income.toFixed(2)} (${result.remaining_base_income.toFixed(2)} salary + ${result.remaining_stock_income.toFixed(2)} stocks)\n`;
        copyText += `Regular Pay Tax Rate: ${result.regular_pay_tax_rate.toFixed(2)}%\n`;
        copyText += `Remaining Planned Taxes: ${result.remaining_planned_taxes.toFixed(2)}\n`;
        copyText += `Remaining Required Taxes: ${result.remaining_required_taxes.toFixed(2)}\n`;
        copyText += `Remaining Taxes Exceeding Requirement: ${result.extra_required_taxes.toFixed(2)}\n`;
        copyText += `Total Deficit As Of Right Now: ${result.deficit_till_now.toFixed(2)}\n`;
        copyText += `Increase Extra Withholding By: ${result.deficit_per_remaining_pay_cycle.toFixed(2)}\n\n`;
      });

      navigator.clipboard.writeText(copyText).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const formatLabel = (str) => {
    const specialLabels = {
      'total_income_till_now': 'Gross Income YTD',

      'total_taxable_income_till_now': 'Taxable Income YTD',
      'federal_taxes_till_now': 'Federal Taxes Paid YTD',
      'yearly_base': 'Annual Base',
      'current_supplemental_tax_rate': 'Supplemental Tax Rate',
      'dcfsa_contribution': 'DCFSA Contribution'
    };

    if (specialLabels[str]) {
      return specialLabels[str];
    }
    const label = str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => {
      if (txt.toLowerCase() === 'hsa') return 'HSA';
      if (txt.toLowerCase() === '401k') return '401K';
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
    return label.replace(/Till Now/g, 'Till Today').replace(/Ytd/g, 'YTD').replace('Current ', '');
  };

  return (
    <>
      <div className="container">
        <h1>Federal Withholding Calculator for W2 Families</h1>
        <p className="intro-text">This tool helps you estimate your federal income tax withholding for the year. By providing details about your income and current withholdings, it calculates your total household tax liability to determine if you are on track for a surplus or deficit. It then suggests individual withholding adjustments, proportional to each person's income, to help you get closer to a $0 balance with the IRS. This tool is for personal use and accuracy isn't guaranteed.</p>
        <div className="global-settings">
          <h3>Global Settings</h3>
          <div className="grid-container">
            <div>
              <label>{formatLabel('remaining_pay_cycles')}<Hint text={dynamicHints.remaining_pay_cycles()} /></label>
              <input type="number" value={remainingPayCycles || ''} onChange={(e) => setRemainingPayCycles(parseFloat(e.target.value) || 0)} />
            </div>
          </div>
        </div>
        <div className="gemini-settings">
          <h3>Gemini Integration</h3>
          <p>A Gemini key can be created here <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer">https://aistudio.google.com/apikey</a>.</p>
          <p>If you provide a Gemini API Key, you can upload Paystubs or images to fetch some of your financial data for withholding calculations (W4).</p>
          <div className="person-card">
            <div className="grid-container">
              <div>
                <label>Gemini API Key</label>
                <input type="text" value={geminiApiKey} onChange={(e) => setGeminiApiKey(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="people-grid">
            {people.map((person, index) => (
              <div key={index} className="person-card">
                <div className="person-header">
                  <div className="person-name-editor">
                    {person.isEditingName ? (
                      <input
                        type="text"
                        className="person-name-input"
                        value={person.name}
                        onChange={(e) => handlePersonChange(index, 'name', e.target.value)}
                        onBlur={() => handleToggleEditName(index)}
                        onKeyDown={(e) => e.key === 'Enter' && handleToggleEditName(index)}
                        autoFocus
                      />
                    ) : (
                      <span className="person-name-display" onClick={() => handleToggleEditName(index)}>
                        {person.name}
                      </span>
                    )}
                    <span className="edit-icon" title="Edit name" onClick={() => handleToggleEditName(index)}>
                      {person.isEditingName ? '✔️' : '✏️'}
                    </span>
                  </div>
                </div>
                <div className="person-card-body">
                  <input
                    type="file" multiple
                    accept=".pdf,image/*"
                    onChange={(e) => handleFileChange(e, index)}
                    disabled={!geminiApiKey || isLoadingGeminiResponse}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="people-section">
          <h2>People</h2>
          <div className="people-grid">
            {people.map((person, index) => (
              <div key={index} className="person-card">
                <div className="person-header">
                  <h3>{person.name}</h3>
                </div>
                <div className="person-card-body">
                  <h4>Annual Salary Information<Hint text={dynamicHints.section_annual_salary()} /></h4>
                  <div className="grid-container">
                    {['annual_income', 'yearly_base'].map(key => (
                      <div key={key}>
                        <label>{formatLabel(key)}<Hint text={dynamicHints[key]()} /></label>
                        <input type="number" value={person[key] || ''} onChange={(e) => handlePersonChange(index, key, e.target.value)} />
                      </div>
                    ))}
                  </div>

                  <h4>Salary Received Till Today<Hint text={dynamicHints.section_salary_received()} /></h4>
                  <div className="grid-container">
                    {['total_income_till_now', 'regular_pay_till_now', 'total_taxable_income_till_now', 'federal_taxes_till_now'].map(key => (
                      <div key={key}>
                        <label>{formatLabel(key)}<Hint text={dynamicHints[key]()} /></label>
                        <input type="number" value={person[key] || ''} onChange={(e) => handlePersonChange(index, key, e.target.value)} />
                      </div>
                    ))}
                  </div>

                  <h4>Deductions & Contributions<Hint text={dynamicHints.section_deductions()} /></h4>
                  <div className="grid-container">
                    <div>
                      <label>{formatLabel('401k_contribution')}<Hint text={dynamicHints['401k_contribution']()} /></label>
                      <input type="number" value={person['401k_contribution'] || ''} onChange={(e) => handlePersonChange(index, '401k_contribution', e.target.value)} />
                    </div>
                    <div>
                      <label>{formatLabel('401k_match_type')}</label>
                      <select
                        value={person['401k_match_type']}
                        onChange={(e) => handlePersonChange(index, '401k_match_type', e.target.value)}
                      >
                        <option value="Multiplier">Multiplier</option>
                        <option value="Percentage of Base">Percentage of Base</option>
                        <option value="Flat">Flat</option>
                      </select>
                    </div>
                    <div>
                      <label>{formatLabel('401k_match_value')}</label>
                      <input type="number" value={person['401k_match_value'] || ''} onChange={(e) => handlePersonChange(index, '401k_match_value', e.target.value)} />
                    </div>
                    <div>
                      <label>{formatLabel('hsa_contribution')}<Hint text={dynamicHints['hsa_contribution']()} /></label>
                      <input type="number" value={person['hsa_contribution'] || ''} onChange={(e) => handlePersonChange(index, 'hsa_contribution', e.target.value)} />
                    </div>
                    <div>
                      <label>{formatLabel('dcfsa_contribution')}<Hint text={dynamicHints['dcfsa_contribution']()} /></label>
                      <input type="number" value={person['dcfsa_contribution'] || ''} onChange={(e) => handlePersonChange(index, 'dcfsa_contribution', e.target.value)} />
                    </div>
                    <div>
                      <label>{formatLabel('transit_contribution')}<Hint text={dynamicHints['transit_contribution']()} /></label>
                      <input type="number" value={person['transit_contribution'] || ''} onChange={(e) => handlePersonChange(index, 'transit_contribution', e.target.value)} />
                    </div>
                  </div>

                  <h4>Current Withholding Details<Hint text={dynamicHints.section_withholding_details()} /></h4>
                  <div className="grid-container">
                    <div>
                      <label>{formatLabel('extra_withholdings')}<Hint text={dynamicHints['extra_withholdings']()} /></label>
                      <input type="number" value={person['extra_withholdings']} onChange={(e) => handlePersonChange(index, 'extra_withholdings', e.target.value)} />
                    </div>
                    <div>
                      <label>{formatLabel('supplemental_tax_rate')}<Hint text={dynamicHints['supplemental_tax_rate']()} /></label>
                      <input type="number" min="0" max="1" step="0.01" value={person['supplemental_tax_rate'] || ''} onChange={(e) => handlePersonChange(index, 'supplemental_tax_rate', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={handleCalculate} className="button-calculate">Calculate</button>
        </div>

        {showResults && results && (
          <div className="results-section">
            <div className="results-header">
              <h2>Results</h2>
              <button onClick={handleCopy} className="button-copy">{copied ? 'Copied!' : 'Copy'}</button>
            </div>
            <div className="results-container">
              <div className="totals-card">
                <h3>Totals</h3>
                <div>Annual Income: {results.total.total_annual_income.toFixed(2)}<Hint text={dynamicHints.total_annual_income(people)} /></div>
                <div>Taxable Income: {results.total.total_annual_taxable_income.toFixed(2)}<Hint text={dynamicHints.total_annual_taxable_income(people, results)} /></div>
                <div>Avg Non-Taxable Income per Person: {results.total.non_taxable_income_per_person.toFixed(2)}<Hint text={dynamicHints.non_taxable_income_per_person(people, results)} /></div>
                <div>Expected Marginal Federal Tax Rate: {results.total.expected_marginal_federal_tax_rate.toFixed(2)}%<Hint text={dynamicHints.expected_marginal_federal_tax_rate(results)} /></div>
                <div>Expected Marginal Federal Tax Rate on Taxable Salary: {results.total.expected_marginal_federal_tax_rate_on_taxable_salary.toFixed(2)}%<Hint text={dynamicHints.expected_marginal_federal_tax_rate_on_taxable_salary(results)} /></div>
              </div>

              {results.people.map((result, index) => (
                <div key={index} className="result-card">
                  <h3>{result.name}</h3>
                  <h4>Personal Details</h4>
                  <div>Annual Income: {result.annual_income.toFixed(2)}<Hint text={dynamicHints.annual_income()} /></div>
                  <div>Non-taxable Income: {result.non_taxable_income.toFixed(2)}<Hint text={dynamicHints.non_taxable_income(people[index])} /></div>
                  <div>Total Income Till Today: {result.total_income_till_now.toFixed(2)}<Hint text={dynamicHints.total_income_till_now()} /></div>
                  <div>Total Taxable Till Today: {result.total_taxable_income_till_now.toFixed(2)}<Hint text={dynamicHints.total_taxable_income_till_now()} /></div>
                  <div>Federal Taxes Till Today: {result.federal_taxes_till_now.toFixed(2)}<Hint text={dynamicHints.federal_taxes_till_now()} /></div>
                  <div>Federal Taxes by Total Income Rate: {result.federal_taxes_by_total_income_rate.toFixed(2)}%<Hint text={dynamicHints.federal_taxes_by_total_income_rate(result)} /></div>
                  <div>Federal Taxes by Total Taxable Income Rate: {result.federal_taxes_by_total_taxable_income_rate.toFixed(2)}%<Hint text={dynamicHints.federal_taxes_by_total_taxable_income_rate(result)} /></div>
                  <div>Supplemental Tax Rate: {result.withholding_rate.toFixed(2)}<Hint text={dynamicHints.supplemental_tax_rate()} /></div>
                  <div>Extra Withholdings: {result.extra_withholdings.toFixed(2)}<Hint text={dynamicHints.extra_withholdings()} /></div>

                  <h4>Suggestion</h4>
                  <div>Federal Tax Deficit Till Today: {result.money_difference_till_now.toFixed(2)}<Hint text={dynamicHints.money_difference_till_now(result)} /></div>
                  <div>Remaining Income: {result.remaining_income.toFixed(2)} (${result.remaining_base_income.toFixed(2)} salary + ${result.remaining_stock_income.toFixed(2)} stocks)<Hint text={dynamicHints.remaining_income(result)} /></div>
                  <div>Regular Pay Tax Rate: {result.regular_pay_tax_rate.toFixed(2)}%<Hint text={dynamicHints.regular_pay_tax_rate(result)} /></div>
                  <div>Remaining Planned Taxes: {result.remaining_planned_taxes.toFixed(2)}<Hint text={dynamicHints.remaining_planned_taxes(result, people[index], remainingPayCycles)} /></div>
                  <div>Remaining Required Taxes: {result.remaining_required_taxes.toFixed(2)}<Hint text={dynamicHints.remaining_required_taxes(result)} /></div>
                  <div>Remaining Taxes Exceeding Requirement: {result.extra_required_taxes.toFixed(2)}<Hint text={dynamicHints.extra_required_taxes(result)} /></div>
                  <div>Total Deficit As Of Right Now: {result.deficit_till_now.toFixed(2)}<Hint text={dynamicHints.deficit_till_now(result)} /></div>
                  <div>Increase Extra Withholding By: ${result.deficit_per_remaining_pay_cycle.toFixed(2)}<Hint text={dynamicHints.deficit_per_remaining_pay_cycle(result, people[index], remainingPayCycles)} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Modal show={showModal} onClose={() => setShowModal(false)} content={modalContent} />
      <Modal show={isLoadingGeminiResponse} content="Waiting for Gemini response..." />
    </>
  );
};

export default WithholdingCalculator;
